import { Order } from '~hooks/useOrders/models';

import { OrderGroup } from './groupLineItemsByOrderJob';

/**
 * Data fetched from order API for display in settlement headers.
 */
interface OrderDisplayData {
  orderId: string;
  /** order.dispatch_number.name */
  dispatchNumberName: string | null;
  /** order.name (used as fallback for dispatch if dispatch_number is null) */
  orderName: string | null;
  /** order.project.name */
  projectName: string | null;
  /** order.order_id (human-readable order ID, not the UUID) */
  orderOrderId: string | null;
  /** order.po_job_number */
  poJobNumber: string | null;
}

/**
 * Fetches order details for unique order IDs and returns display data.
 *
 * @param orderIds - Array of unique order IDs to fetch
 * @param getOrderById - Function to fetch a single order by ID
 * @returns Map of orderId to OrderDisplayData
 */
export async function fetchOrderDisplayData(
  orderIds: string[],
  getOrderById: (id: string) => Promise<Order>,
): Promise<Map<string, OrderDisplayData>> {
  const results = new Map<string, OrderDisplayData>();

  // Fetch all orders in parallel
  const orderPromises = orderIds.map(async (orderId) => {
    try {
      const order = await getOrderById(orderId);
      return {
        orderId,
        dispatchNumberName: order.dispatchNumber?.name ?? null,
        orderName: order.name ?? null,
        projectName: order.projectName ?? null,
        orderOrderId: order.orderId ?? null,
        poJobNumber: order.poJobNumber ?? null,
      };
    } catch {
      // If fetch fails, return null values
      return {
        orderId,
        dispatchNumberName: null,
        orderName: null,
        projectName: null,
        orderOrderId: null,
        poJobNumber: null,
      };
    }
  });

  const orderDataList = await Promise.all(orderPromises);

  for (const orderData of orderDataList) {
    results.set(orderData.orderId, orderData);
  }

  return results;
}

/**
 * Enriches order groups with fetched display data.
 *
 * @param orderGroups - Array of order groups to enrich
 * @param displayDataMap - Map of orderId to OrderDisplayData from fetchOrderDisplayData
 * @returns New array of order groups with enriched data
 */
export function enrichOrderGroupsWithDisplayData(
  orderGroups: OrderGroup[],
  displayDataMap: Map<string, OrderDisplayData>,
): OrderGroup[] {
  return orderGroups.map((group) => {
    const displayData = displayDataMap.get(group.orderId);
    if (!displayData) {
      return group;
    }

    return {
      ...group,
      dispatchNumberName: displayData.dispatchNumberName,
      orderName: displayData.orderName,
      projectName: displayData.projectName,
      orderOrderId: displayData.orderOrderId,
      poJobNumber: displayData.poJobNumber,
    };
  });
}

/**
 * Extracts unique order IDs from order groups.
 *
 * @param orderGroups - Array of order groups
 * @returns Array of unique order IDs
 */
export function extractUniqueOrderIds(orderGroups: OrderGroup[]): string[] {
  return [...new Set(orderGroups.map((group) => group.orderId))];
}
